/**
 * The front desk's switches (Settings › Reception), in plain English.
 *
 * Settings › Reception is the ONE place reception mode is set up: each
 * switch sets one or more capabilities on the business's Front desk role,
 * which is what a device locked in reception mode can do (never more than
 * whoever is signed in on it).
 *
 * MIRRORS beu-node-v2 receptionCaps.DESK_SWITCHES: keys and caps MUST match.
 *  - every capability is set by exactly one switch, except `catalogue`, which
 *    is on whenever any "Your menu" switch is on;
 *  - payouts, permissions, billing and locations are the owner's: never offered.
 */

export const SWITCH_GROUPS = [
  { id: 'bookings', name: 'Bookings' },
  { id: 'clients', name: 'Clients' },
  { id: 'menu', name: 'Your menu' },
  { id: 'money', name: 'Money' },
  { id: 'team', name: 'Team' },
  { id: 'business', name: 'Business' },
];

export const DESK_SWITCHES = [
  // Bookings
  { key: 'diary', group: 'bookings', caps: ['diary_all', 'clients_all'], label: 'Diary', hint: 'See and book appointments for any client' },
  { key: 'pos_page', group: 'bookings', caps: ['pos_page'], label: 'Point of Sale', hint: 'The till screen, for walk-in sales' },
  { key: 'take_payments', group: 'bookings', caps: ['pos'], label: 'Take payments', hint: "Check out anyone's booking" },
  { key: 'discounts', group: 'bookings', caps: ['discounts'], label: 'Discounts', hint: 'Take money off a booking or sale' },
  { key: 'refunds', group: 'bookings', caps: ['refunds'], label: 'Refunds', hint: 'Give money back on salon sales' },
  // Clients
  { key: 'clients', group: 'clients', caps: ['clients_page'], label: 'Find and add clients', hint: 'The Clients screen: search, view and add' },
  { key: 'client_notes', group: 'clients', caps: ['client_notes'], label: 'Client notes and forms', hint: 'Notes, visit history, and consultation and medical forms' },
  { key: 'client_messages', group: 'clients', caps: ['client_messages'], label: 'Client messages', hint: 'Read and reply to texts and emails from clients' },
  { key: 'clients_export', group: 'clients', caps: ['clients_export'], label: 'Export client lists', hint: 'Download clients as a spreadsheet' },
  // Your menu
  { key: 'services', group: 'menu', caps: ['services'], label: 'Services and prices', hint: 'Change services, prices and categories' },
  { key: 'products', group: 'menu', caps: ['products'], label: 'Products', hint: 'Products, orders and the online store' },
  { key: 'stock', group: 'menu', caps: ['stock'], label: 'Stock take', hint: 'Count and move stock' },
  { key: 'gift_cards', group: 'menu', caps: ['gift_cards'], label: 'Gift cards (sell)', hint: 'Gift card designs and sales' },
  { key: 'gift_card_admin', group: 'menu', caps: ['gift_card_admin'], label: 'Gift card changes', hint: 'Issue, void or change balances' },
  { key: 'forms', group: 'menu', caps: ['forms'], label: 'Forms', hint: 'Edit your consultation and booking forms' },
  { key: 'resources', group: 'menu', caps: ['resources'], label: 'Rooms and equipment', hint: 'Rooms, beds and equipment that bookings use' },
  { key: 'taxes', group: 'menu', caps: ['taxes'], label: 'Taxes', hint: 'VAT on services and products' },
  // Money
  { key: 'cashup', group: 'money', caps: ['cashup'], label: 'Cash-up', hint: 'Count the till at the end of the day' },
  { key: 'reports', group: 'money', caps: ['reports'], label: 'Finance and reports', hint: 'Takings, transactions and reports' },
  // Team
  { key: 'schedules', group: 'team', caps: ['schedules'], label: 'Schedules', hint: 'Working hours and time off' },
  { key: 'staff', group: 'team', caps: ['team', 'staff_pay'], label: 'Staff and pay', hint: 'Add or change team members and their pay' },
  // Business
  { key: 'marketing', group: 'business', caps: ['marketing'], label: 'Marketing', hint: 'Campaigns, automations and loyalty' },
  { key: 'discount_codes', group: 'business', caps: ['discount_codes'], label: 'Discount codes', hint: "Make and change the salon's codes" },
  { key: 'settings', group: 'business', caps: ['settings', 'salon_details', 'booking_settings', 'card_readers', 'diary_sharing', 'notification_settings', 'data_import', 'finish_setup'], label: 'Settings', hint: 'Business details, booking rules, card readers, notifications and imports' },
  { key: 'ai_receptionist', group: 'business', caps: ['ai_receptionist'], label: 'AI receptionist and business numbers', hint: 'Calls, texts and the receptionist inbox' },
  { key: 'account', group: 'business', caps: ['account'], label: 'Your profile and password', hint: 'Change the profile of whoever is signed in' },
  { key: 'bailey', group: 'business', caps: ['bailey'], label: 'Bailey', hint: "Help at the desk. Bailey only sees and does what's switched on here" },
  { key: 'bailey_approvals', group: 'business', caps: ['bailey_approvals'], label: "Approving Bailey's suggestions", hint: 'Let the desk OK messages, sends and anything that costs money' },
];

export const NOT_OFFERED = ['payouts', 'permissions', 'billing', 'locations'];
export const MENU_CAPS = DESK_SWITCHES.filter((s) => s.group === 'menu').flatMap((s) => s.caps);
const BY_KEY = DESK_SWITCHES.reduce((m, s) => { m[s.key] = s; return m; }, {});

export const switchByKey = (key) => BY_KEY[key] || null;

/** { key: on } for a cap list. A switch is on when any of its caps is. */
export function switchesFromCaps(caps) {
  const have = new Set(caps || []);
  const out = {};
  DESK_SWITCHES.forEach((s) => { out[s.key] = s.caps.some((c) => have.has(c)); });
  return out;
}

/** The caps a set of switches gives (a list of keys, or a { key: on } map). */
export function switchesToCaps(on) {
  const keys = Array.isArray(on) ? on : Object.keys(on || {}).filter((k) => on[k] === true);
  const caps = new Set();
  keys.forEach((k) => { const s = BY_KEY[k]; if (s) s.caps.forEach((c) => caps.add(c)); });
  if (MENU_CAPS.some((c) => caps.has(c))) caps.add('catalogue');
  return [...caps];
}

/** Flip one switch on a cap list: only its caps (and catalogue, for "Your menu") change. */
export function applySwitch(caps, key, on) {
  const s = BY_KEY[key];
  if (!s) return (caps || []).slice();
  const next = new Set(caps || []);
  s.caps.forEach((c) => { if (on) next.add(c); else next.delete(c); });
  if (s.group === 'menu') {
    if (MENU_CAPS.some((c) => next.has(c))) next.add('catalogue'); else next.delete('catalogue');
  }
  NOT_OFFERED.forEach((c) => next.delete(c));
  return [...next];
}

/**
 * The groups to show: a switch appears when this person may offer it, or
 * when it's already on (so nothing the desk can do is ever invisible here).
 */
export function visibleGroups(switches, offered) {
  const can = new Set(offered || DESK_SWITCHES.map((s) => s.key));
  return SWITCH_GROUPS
    .map((g) => ({ ...g, switches: DESK_SWITCHES.filter((s) => s.group === g.id && (can.has(s.key) || (switches && switches[s.key]))) }))
    .filter((g) => g.switches.length);
}
